import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/useSession';
import { supportedLanguages } from '../lib/languages';

const langName = (code) =>
  supportedLanguages.find((l) => l.code === code)?.name || code || '—';

const emptyDraft = { word: '', translation: '', pos: '', example: '', language: 'spanish' };

const VocabularyBook = () => {
  const { session, loading } = useSession();
  const navigate = useNavigate();

  const [cards, setCards] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState('all');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = adding
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!loading && !session) navigate('/login', { replace: true });
  }, [loading, session, navigate]);

  const loadCards = useCallback(async () => {
    if (!session) return;
    setFetching(true);
    const { data, error } = await supabase
      .from('vocab_cards')
      .select('id, language, word, lemma, pos, translation, example, source, created_at')
      .order('created_at', { ascending: false });
    if (!error && data) setCards(data);
    setFetching(false);
  }, [session]);

  useEffect(() => {
    if (session) loadCards();
  }, [session, loadCards]);

  const languagesInBook = useMemo(() => {
    const set = [...new Set(cards.map((c) => c.language).filter(Boolean))];
    return set.sort((a, b) => langName(a).localeCompare(langName(b)));
  }, [cards]);

  const visibleCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter((c) => {
      if (langFilter !== 'all' && c.language !== langFilter) return false;
      if (!q) return true;
      return (
        (c.word || '').toLowerCase().includes(q) ||
        (c.translation || '').toLowerCase().includes(q) ||
        (c.example || '').toLowerCase().includes(q)
      );
    });
  }, [cards, search, langFilter]);

  const openAdd = () => {
    setEditingId(null);
    setDraft({ ...emptyDraft, language: languagesInBook[0] || 'spanish' });
    setFormError('');
    setEditorOpen(true);
  };

  const openEdit = (card) => {
    setEditingId(card.id);
    setDraft({
      word: card.word || '',
      translation: card.translation || '',
      pos: card.pos || '',
      example: card.example || '',
      language: card.language || 'spanish',
    });
    setFormError('');
    setEditorOpen(true);
  };

  const saveDraft = async (e) => {
    e.preventDefault();
    if (!draft.word.trim() || !draft.translation.trim()) {
      setFormError('Word and translation are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    const payload = {
      user_id: session.user.id,
      language: draft.language,
      word: draft.word.trim(),
      lemma: draft.word.trim(),
      pos: draft.pos.trim() || null,
      translation: draft.translation.trim(),
      example: draft.example.trim() || null,
      source: editingId ? 'manual-edit' : 'manual',
    };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('vocab_cards').update(payload).eq('id', editingId));
    } else {
      ({ error } = await supabase
        .from('vocab_cards')
        .upsert(payload, {
          onConflict: 'user_id,lang_norm,lemma_norm,pos_norm',
          ignoreDuplicates: true,
        }));
    }
    setSaving(false);
    if (error) {
      setFormError(error.message || 'Could not save. Try again.');
      return;
    }
    setEditorOpen(false);
    loadCards();
  };

  const deleteCard = async (id) => {
    const prev = cards;
    setCards((c) => c.filter((x) => x.id !== id)); // optimistic
    const { error } = await supabase.from('vocab_cards').delete().eq('id', id);
    if (error) setCards(prev);
  };

  return (
    <div className="min-h-screen app-shell px-4 py-8 transition-colors duration-300">
      <div className="pointer-events-none fixed inset-0 opacity-65 mix-blend-screen" style={{ background: 'radial-gradient(60% 50% at 20% 30%, rgba(139,208,255,0.10), transparent), radial-gradient(45% 40% at 80% 20%, rgba(74,163,255,0.08), transparent)' }}></div>

      <div className="relative mx-auto max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <Link to="/" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors">
            ← ExtractStudy
          </Link>
          <button
            onClick={openAdd}
            className="btn-primary text-gray-900 py-2 px-4 rounded-lg text-sm font-medium transition duration-200"
          >
            + Add flashcard
          </button>
        </div>
        <h1 className="section-title brand-title text-3xl sm:text-4xl font-bold mb-1">
          Vocabulary Book
        </h1>
        <p className="text-[var(--muted)] text-sm mb-6">
          Every word you generate while logged in is saved here automatically — no repeats.
        </p>

        {/* Controls */}
        <div className="mb-5 space-y-3">
          <input
            type="text"
            className="w-full p-2.5 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm"
            placeholder="Search words, translations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {languagesInBook.length > 0 && (
            <div className="flex gap-2 overflow-x-auto whitespace-nowrap -mx-1 px-1 pb-1 custom-scrollbar">
              {['all', ...languagesInBook].map((code) => {
                const active = langFilter === code;
                return (
                  <button
                    key={code}
                    onClick={() => setLangFilter(code)}
                    className={`flex-shrink-0 text-xs font-medium rounded-full px-3.5 py-1.5 border transition-colors duration-150 ${
                      active
                        ? 'text-gray-900 border-transparent'
                        : 'text-[var(--muted)] border-[var(--border)] hover:text-[var(--text)]'
                    }`}
                    style={active ? { backgroundColor: 'var(--accent)' } : undefined}
                  >
                    {code === 'all' ? 'All' : langName(code)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* List */}
        {fetching ? (
          <p className="text-[var(--muted)] text-sm py-12 text-center">Loading your words…</p>
        ) : visibleCards.length === 0 ? (
          <div className="glass-panel rounded-2xl border border-[var(--border)] p-10 text-center">
            <p className="text-[var(--text)] text-sm mb-1">
              {cards.length === 0 ? 'No saved words yet.' : 'Nothing matches your filters.'}
            </p>
            <p className="text-[var(--muted)] text-sm">
              {cards.length === 0
                ? 'Generate flashcards from a passage while logged in and they’ll appear here.'
                : 'Try a different search or language.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleCards.map((card) => (
              <div
                key={card.id}
                className="group glass-panel rounded-xl border border-[var(--border)] px-4 py-3 flex items-start gap-3 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[var(--text)] font-semibold">{card.word}</span>
                    {card.pos && (
                      <span className="text-[var(--muted)] text-xs italic">{card.pos}</span>
                    )}
                    <span className="text-[var(--muted)] text-[10px] uppercase tracking-wide border border-[var(--border)] rounded px-1.5 py-0.5">
                      {langName(card.language)}
                    </span>
                  </div>
                  <div className="text-[var(--text)] text-sm mt-0.5">{card.translation}</div>
                  {card.example && (
                    <div className="text-[var(--muted)] text-xs italic mt-1">{card.example}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(card)}
                    className="text-[var(--muted)] hover:text-[var(--accent)] p-1.5 rounded-lg transition-colors"
                    aria-label="Edit"
                    title="Edit"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => deleteCard(card.id)}
                    className="text-[var(--muted)] hover:text-red-400 p-1.5 rounded-lg transition-colors"
                    aria-label="Delete"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {editorOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-3">
          <div
            className="glass-panel p-6 rounded-2xl w-full max-w-md shadow-xl border border-[var(--border)]"
            style={{ boxShadow: '0 0 30px rgba(0,0,0,0.35)', animation: 'slideUp 0.3s ease-out' }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[var(--accent)] section-title">
                {editingId ? 'Edit flashcard' : 'Add flashcard'}
              </h2>
              <button
                className="text-[var(--muted)] hover:text-[var(--text)] transition-colors"
                onClick={() => setEditorOpen(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={saveDraft} className="space-y-3">
              <input
                type="text"
                className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm"
                placeholder="Word"
                value={draft.word}
                onChange={(e) => setDraft({ ...draft, word: e.target.value })}
                autoFocus
              />
              <input
                type="text"
                className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm"
                placeholder="Translation"
                value={draft.translation}
                onChange={(e) => setDraft({ ...draft, translation: e.target.value })}
              />
              <div className="flex gap-3">
                <input
                  type="text"
                  className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm"
                  placeholder="Type (noun, verb…)"
                  value={draft.pos}
                  onChange={(e) => setDraft({ ...draft, pos: e.target.value })}
                />
                <select
                  className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:outline-none text-sm"
                  value={draft.language}
                  onChange={(e) => setDraft({ ...draft, language: e.target.value })}
                >
                  {supportedLanguages.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              </div>
              <textarea
                className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none text-sm resize-none h-20"
                placeholder="Example sentence (optional)"
                value={draft.example}
                onChange={(e) => setDraft({ ...draft, example: e.target.value })}
              />
              {formError && <p className="text-red-400 text-xs">{formError}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  className="btn-secondary py-2 px-4 rounded-lg text-sm font-medium border border-[var(--border)]"
                  onClick={() => setEditorOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary text-gray-900 py-2 px-4 rounded-lg text-sm font-medium transition duration-200 disabled:opacity-60"
                >
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyBook;
